<template>
  <Transition
    appear
    enter-active-class="transition transform duration-400 ease-in-out"
    enter-from-class="translate-y-full md:translate-y-0 md:translate-x-full opacity-0"
    leave-active-class="transition transform duration-200 ease-in-out"
    leave-to-class="translate-y-full md:translate-y-0 md:translate-x-full opacity-0"
    @after-leave="onTransitionComplete"
  >
    <div
      v-if="isVisible"
      :aria-describedby="aria.descriptionId"
      :aria-labelledby="aria.titleId"
      aria-modal="true"
      class="shadow-long fixed bottom-0 z-10 md:bottom-60 md:right-24"
      role="dialog"
    >
      <div class="relative">
        <RevButtonIcon
          :aria-label="i18n(translations.closeLabel)"
          class="absolute right-12 top-12 z-[1] bg-transparent"
          :icon="IconCross"
          size="small"
          variant="primary"
          @click="handleClose"
        />

        <!-- Hidden elements for screen readers -->
        <span :id="aria.titleId" class="sr-only">{{
          i18n(translations.ariaTitle)
        }}</span>
        <span :id="aria.descriptionId" class="sr-only">{{
          i18n(translations.ariaDescription)
        }}</span>

        <slot></slot>
      </div>
    </div>
  </Transition>
</template>

<script lang="ts" setup>
import { useRoute } from '#imports'
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { IconCross } from '@ds/icons/IconCross'

import translations from './BasePopup.translations'

const i18n = useI18n()
const route = useRoute()

const props = withDefaults(
  defineProps<{
    displayPopup: boolean
  }>(),
  {
    displayPopup: false,
  },
)

const emit = defineEmits(['closed'])

const aria = {
  titleId: 'lead-collection-aria-title-id',
  descriptionId: 'lead-collection-aria-description',
}

const isVisible = ref(false)

function show() {
  isVisible.value = true
}

function hide() {
  isVisible.value = false
}

function handleClose() {
  hide()
}

const handleKeyDown = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && isVisible.value) {
    handleClose()
  }
}

function onTransitionComplete() {
  if (!isVisible.value) {
    emit('closed')
  }
}

watch(
  () => props.displayPopup,
  (displayPopup) => {
    const action = displayPopup ? show : hide
    action()
  },
  { immediate: true },
)

watch(
  () => route.fullPath,
  () => {
    handleClose()
  },
)

onMounted(() => {
  window.addEventListener('keydown', handleKeyDown)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeyDown)
})
</script>
